/**
 * @file Components/ProtectedRoute.jsx
 * @description Route guard component. Verifies Firebase auth state, fetches CSRF tokens,
 * checks admin privileges, and redirects unauthenticated users to login.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import api from '../services/api';

// Security headers middleware
const SecurityHeaders = () => {
    useEffect(() => {
        // Add security headers to all responses
        const meta = document.createElement('meta');
        meta.httpEquiv = "Content-Security-Policy";
        meta.content = "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self';";
        document.head.appendChild(meta);

        // Add other security headers
        const headers = [
            { httpEquiv: 'X-Content-Type-Options', content: 'nosniff' },
            { httpEquiv: 'X-Frame-Options', content: 'DENY' },
            { httpEquiv: 'X-XSS-Protection', content: '1; mode=block' },
            { httpEquiv: 'Referrer-Policy', content: 'strict-origin-when-cross-origin' },
            { name: 'Strict-Transport-Security', content: 'max-age=31536000; includeSubDomains; preload' },
            { name: 'Permissions-Policy', content: 'camera=(), microphone=(), geolocation=(), payment=()' }
        ];

        headers.forEach(header => {
            const element = document.createElement('meta');
            Object.entries(header).forEach(([key, value]) => {
                element.setAttribute(key, value);
            });
            document.head.appendChild(element);
        });

        return () => {
            // Clean up when component unmounts
            try {
                if (meta && meta.parentNode) {
                    document.head.removeChild(meta);
                }
                headers.forEach(header => {
                    const selector = header.httpEquiv ? `meta[http-equiv="${header.httpEquiv}"]` : `meta[name="${header.name}"]`;
                    const elements = document.querySelectorAll(selector);
                    elements.forEach(el => {
                        if (el && el.parentNode) {
                            document.head.removeChild(el);
                        }
                    });
                });
            } catch (error) {
                console.warn('Error cleaning up security headers:', error);
            }
        };
    }, []);

    return null;
};

// Rate limiting and CSRF protection
const useSecurity = () => {
    const [csrfToken, setCsrfToken] = useState('');
    const [requestCount, setRequestCount] = useState(0);
    const lastRequestTime = React.useRef(Date.now());

    // Initialize CSRF token
    useEffect(() => {
        const fetchCsrfToken = async () => {
            try {
                const response = await api.get('/auth/csrf-token');
                setCsrfToken(response.data.csrfToken);
                api.defaults.headers.common['X-CSRF-Token'] = response.data.csrfToken;
            } catch (error) {
                console.error('Error fetching CSRF token:', error);
            }
        };
        fetchCsrfToken();
    }, []);

    // Rate limiting
    const checkRateLimit = useCallback(() => {
        const now = Date.now();
        const timeElapsed = now - lastRequestTime.current;
        lastRequestTime.current = now;

        // Reset counter if more than 1 minute has passed
        if (timeElapsed > 60000) {
            setRequestCount(1);
            return true;
        }

        // Allow up to 100 requests per minute
        if (requestCount >= 100) {
            return false;
        }

        setRequestCount(prev => prev + 1);
        return true;
    }, [requestCount]);

    return { csrfToken, checkRateLimit };
};

const ProtectedRoute = ({ children, requireAdmin = false }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    // Handle auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                // Read existing stored user data (may contain isAdmin from backend login)
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                
                // Only update Firebase-specific fields, preserve backend data (isAdmin, role, token)
                const mergedData = {
                    ...storedUser,
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName || storedUser.displayName,
                    photoURL: user.photoURL || storedUser.photoURL,
                    emailVerified: user.emailVerified,
                };
                
                localStorage.setItem('user', JSON.stringify(mergedData));
                setIsAuthenticated(true);
                setIsAdmin(mergedData.isAdmin === true || mergedData.role === 'admin');
            } else {
                // Check if user logged in via backend only (no Firebase)
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                if (storedUser.token && (storedUser.isAdmin || storedUser.role === 'admin')) {
                    setIsAuthenticated(true);
                    setIsAdmin(true);
                } else {
                    localStorage.removeItem('user');
                    setIsAuthenticated(false);
                    setIsAdmin(false);
                }
            }
            setIsLoading(false);
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, []);

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-red-500 text-center p-6 bg-white rounded-lg shadow-lg">
                    <h2 className="text-2xl font-bold mb-2">Authentication Error</h2>
                    <p className="mb-4">{error}</p>
                    <button 
                        onClick={() => window.location.href = '/login'}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                    >
                        Return to Login
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Checking authentication...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        // Store the current location to redirect back after login
        const currentPath = location.pathname + location.search;
        return <Navigate to="/login" state={{ from: currentPath }} replace />;
    }
    
    if (requireAdmin && !isAdmin) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center p-6 bg-white rounded-lg shadow-lg">
                    <h2 className="text-2xl font-bold text-red-500 mb-4">Access Denied</h2>
                    <p className="mb-4">You don't have permission to access this page.</p>
                    <button 
                        onClick={() => navigate('/')}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                    >
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    return children;
};

export default ProtectedRoute;